/**
 * A dropdown you can type into.
 *
 * Every list of things in the portal — people, sites, jobs, clients, categories — used
 * to be a native <select>: fine for five entries, useless for fifty, and it could only
 * show a line of plain text, so two people with the same name were indistinguishable.
 * This shows a picture and a second line for each option and filters as you type
 * (accents and case ignored). Arrow keys, Enter and Escape work as expected.
 *
 * The list opens in a layer on top of the page rather than inside the form, so a
 * dialog's scrolling body cannot clip it.
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useI18n } from '@/i18n';

export interface ComboOption {
  value: string;
  label: string;
  /** A second, quieter line: an email, an address, a reference. */
  sublabel?: string;
  /** A small picture or icon before the label. */
  leading?: ReactNode;
  /** Extra words the search should match that are not shown. */
  keywords?: string;
  /** Options with the same group are listed together under that heading. */
  group?: string;
}

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export function Combobox({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  className = '',
  ariaLabel,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: ComboOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const listId = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<{ top: number; left: number; width: number; maxHeight: number; up: boolean }>();

  const selected = options.find((o) => o.value === value);

  const visible = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return options;
    const words = q.split(/\s+/);
    return options.filter((o) => {
      const hay = fold(`${o.label} ${o.sublabel ?? ''} ${o.keywords ?? ''} ${o.group ?? ''}`);
      return words.every((w) => hay.includes(w));
    });
  }, [options, query]);

  // Where the list goes: under the button, or above it when there is no room below.
  const measure = () => {
    const r = button.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < 260 && above > below;
    setPlace({
      top: up ? r.top - 4 : r.bottom + 4,
      left: Math.min(r.left, window.innerWidth - Math.max(r.width, 300) - 8),
      width: Math.max(r.width, 300),
      maxHeight: Math.min(360, up ? above : below),
      up,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const index = Math.max(0, visible.findIndex((o) => o.value === value));
    setActive(index);
    // Focus the search once the panel exists.
    requestAnimationFrame(() => search.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panel.current?.contains(target) || button.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the highlighted option in view while arrowing through a long list.
  useEffect(() => {
    panel.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (o: ComboOption) => {
    onChange(o.value);
    setOpen(false);
    setQuery('');
    button.current?.focus();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(visible.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = visible[active];
      if (o) pick(o);
    } else if (e.key === 'Escape') {
      // Close the list only — not the dialog it sits in.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      button.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  let lastGroup: string | undefined;

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`input flex items-center gap-2 text-start ${disabled ? 'opacity-60' : ''} ${className}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {selected?.leading ? <span className="shrink-0 flex">{selected.leading}</span> : null}
        <span className={`truncate flex-1 ${selected ? '' : 'text-subtle'}`}>
          {selected ? selected.label : (placeholder ?? t('common.select'))}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden className="shrink-0 text-muted">
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {open && place
        ? createPortal(
            <div
              ref={panel}
              className="fixed z-[1000] rounded-lg border border-border bg-surface shadow-xl flex flex-col overflow-hidden"
              style={{
                left: place.left,
                width: place.width,
                maxHeight: place.maxHeight,
                ...(place.up ? { bottom: window.innerHeight - place.top } : { top: place.top }),
              }}
              onKeyDown={onKey}
            >
              <div className="p-2 border-b border-border">
                <input
                  ref={search}
                  className="input h-9"
                  value={query}
                  placeholder={searchPlaceholder ?? t('common.typeToSearch')}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  aria-controls={listId}
                  aria-activedescendant={visible[active] ? `${listId}-${active}` : undefined}
                />
              </div>
              <ul id={listId} role="listbox" className="overflow-y-auto py-1">
                {visible.length === 0 ? (
                  <li className="px-3 py-3 text-sm text-muted">{t('common.noMatches')}</li>
                ) : (
                  visible.map((o, i) => {
                    const heading = o.group && o.group !== lastGroup ? o.group : null;
                    lastGroup = o.group;
                    return (
                      <li key={`${o.group ?? ''}:${o.value}`}>
                        {heading ? (
                          <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">
                            {heading}
                          </div>
                        ) : null}
                        <div
                          id={`${listId}-${i}`}
                          role="option"
                          aria-selected={o.value === value}
                          data-index={i}
                          className={`mx-1 px-2 py-1.5 rounded flex items-center gap-2.5 cursor-pointer ${
                            i === active ? 'bg-surface-alt' : ''
                          } ${o.value === value ? 'font-semibold' : ''}`}
                          onMouseEnter={() => setActive(i)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pick(o)}
                        >
                          {o.leading ? <span className="shrink-0 flex">{o.leading}</span> : null}
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm truncate">{o.label}</span>
                            {o.sublabel ? <span className="block text-xs text-muted truncate">{o.sublabel}</span> : null}
                          </span>
                          {o.value === value ? (
                            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden className="text-primary shrink-0">
                              <path d="M3 7.5 6 10l5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                          ) : null}
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
